import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { HeartPulse, X } from 'lucide-react';
import HealthRecordTab from '../patientTabs/HealthRecordTab';

// "PATIENT HEALTH RECORD" DRAWER (round 9, owner 24 Sep 2026).
//
// A doctor reads the patient's full health record INSIDE the open visit,
// never by leaving it: leaving is how half-typed notes were lost. So this is
// an overlay on the right (≈880 px / 80vw on a desktop since 24 Sep 2026,
// was 480 px; full width on a phone) portalled to
// <body>; the visit screen behind it is not unmounted, re-rendered or
// scrolled, and whatever is typed there is exactly where it was on close.
//
// The body IS HealthRecordTab in visit mode (`appointmentId`) — the same
// header, section chips and cards the patient's tab shows, plus the
// "New since last visit" filter and "Add entry" with this appointment. One
// component, so the drawer can never drift from the tab.
//
// Esc, the ✕ and a click on the dimmed backdrop all close it.

function HealthRecordDrawer({ isOpen, onClose, patientId, appointmentId }) {
    const { t } = useTranslation();
    const closeRef = useRef(null);
    // Held in a ref so a parent passing a fresh arrow each render does not
    // re-run the effect below — which would pull focus out of the Add form
    // on every keystroke.
    const onCloseRef = useRef(onClose);
    useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onCloseRef.current?.(); };
        window.addEventListener('keydown', onKey);
        // Focus lands inside the dialog so Esc and Tab work from the keyboard.
        closeRef.current?.focus();
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen]);

    if (!isOpen || typeof document === 'undefined') return null;

    return createPortal(
        <div className="fixed inset-0 z-[60] flex justify-end" data-testid="health-record-drawer-root">
            <div
                data-testid="health-record-drawer-backdrop"
                className="absolute inset-0 bg-black/30 transition-opacity"
                onClick={onClose}
                aria-hidden="true"
            />
            <aside
                role="dialog"
                aria-modal="true"
                aria-labelledby="health-record-drawer-title"
                data-testid="health-record-drawer"
                className="relative flex h-full w-full sm:w-[min(880px,80vw)] flex-col bg-white shadow-2xl"
            >
                <header className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
                    <h2 id="health-record-drawer-title" className="flex items-center gap-2 text-base font-bold text-[#2D8A8A]">
                        <HeartPulse className="h-5 w-5" aria-hidden="true" />
                        {t('healthRecord.drawer.title', 'Patient health record')}
                    </h2>
                    <button
                        ref={closeRef}
                        type="button"
                        data-testid="health-record-drawer-close"
                        onClick={onClose}
                        aria-label={t('healthRecord.drawer.close', 'Close')}
                        className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 cursor-pointer"
                    >
                        <X className="h-5 w-5" aria-hidden="true" />
                    </button>
                </header>
                <div className="flex-1 overflow-y-auto p-4">
                    <HealthRecordTab patientId={patientId} appointmentId={appointmentId} />
                </div>
            </aside>
        </div>,
        document.body
    );
}

export default HealthRecordDrawer;
